import { money } from "@stb/core/money"

import { isValidAbn } from "./abn.ts"
import { calculateDocumentGst, type AmountsAre } from "./gst.ts"

/**
 * Sales documents (PRD R2, T412).
 *
 * Source: ATO, "Tax invoices"
 * (https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/tax-invoices).
 * A tax invoice for a taxable sale of A$1,000 or more (GST inclusive) must show the buyer's
 * identity or ABN; below that only the seller's identity, ABN, date, description, GST amount and
 * the words "tax invoice" are needed. The threshold applies in financial year 2026-27 (unchanged
 * since 2000). Recorded 2026-10-27 from the ATO guidance already cited in `tax-invoice.ts`; the
 * ATO page returned 403 to automated checks, so re-confirm at the next annual rate review.
 */

/** A$1,000.00 GST inclusive, in cents. A tax invoice at or above this amount names the buyer. */
export const INVOICE_BUYER_IDENTITY_THRESHOLD_MINOR = 100_000

export type InvoiceDocumentTitle = "tax_invoice" | "invoice"
export type InvoiceTaxCode = "GST" | "GST_FREE"

export type InvoiceIssueCode =
  | "BUYER_IDENTITY_REQUIRED"
  | "SELLER_NAME_REQUIRED"
  | "SELLER_ABN_REQUIRED"
  | "NO_LINES"

export interface InvoiceIssue {
  code: InvoiceIssueCode
  /** Field the user should fix, for the form. */
  field: "buyer" | "seller_name" | "seller_abn" | "lines"
}

export interface InvoiceValidationInput {
  documentTitle: InvoiceDocumentTitle
  totalMinor: number
  /** The seller's business name; an invoice always names who issued it. */
  sellerName: string | null
  sellerAbn: string | null
  buyerName: string | null
  buyerAbn: string | null
  lineCount: number
}

/**
 * What must be fixed before an invoice can be sent. Drafts may have issues; Send refuses them.
 * Issues come in a fixed order: seller name, seller ABN, lines, buyer.
 */
export function validateInvoice(input: InvoiceValidationInput): InvoiceIssue[] {
  const issues: InvoiceIssue[] = []
  if (!input.sellerName?.trim()) {
    issues.push({ code: "SELLER_NAME_REQUIRED", field: "seller_name" })
  }
  if (!input.sellerAbn || !isValidAbn(input.sellerAbn)) {
    issues.push({ code: "SELLER_ABN_REQUIRED", field: "seller_abn" })
  }
  if (input.lineCount < 1) issues.push({ code: "NO_LINES", field: "lines" })
  const hasBuyer = Boolean(input.buyerName?.trim()) || Boolean(input.buyerAbn?.trim())
  if (
    input.documentTitle === "tax_invoice" &&
    input.totalMinor >= INVOICE_BUYER_IDENTITY_THRESHOLD_MINOR &&
    !hasBuyer
  ) {
    issues.push({ code: "BUYER_IDENTITY_REQUIRED", field: "buyer" })
  }
  return issues
}

/** Line amount in cents: quantity in thousandths times the unit amount, rounded half up. */
export function lineAmountMinor(quantityMilli: number, unitAmountMinor: number): number {
  const product = BigInt(quantityMilli) * BigInt(unitAmountMinor)
  const sign = product < 0n ? -1n : 1n
  return Number(sign * ((sign * product + 500n) / 1000n))
}

export interface InvoiceLineInput {
  quantityMilli: number
  unitAmountMinor: number
  taxCode: InvoiceTaxCode
}

export interface InvoiceTotalsOptions {
  amountsAre: AmountsAre
  rounding: "per-line" | "per-document"
  /** A seller that is not GST registered charges no GST whatever the line codes say. */
  gstRegistered: boolean
}

export interface InvoiceLineTotals {
  netMinor: number
  gstMinor: number
  grossMinor: number
}

export interface InvoiceTotals {
  lines: InvoiceLineTotals[]
  subtotalMinor: number
  gstMinor: number
  totalMinor: number
  /** Document GST minus the sum of line GST; 0 for per-line rounding. */
  roundingAdjustmentMinor: number
}

/** Line and document amounts, using `calculateDocumentGst` so purchases and sales round alike. */
export function computeInvoiceTotals(
  lines: readonly InvoiceLineInput[],
  options: InvoiceTotalsOptions,
): InvoiceTotals {
  const result = calculateDocumentGst(
    lines.map((line) => ({
      amount: money(lineAmountMinor(line.quantityMilli, line.unitAmountMinor), "AUD"),
      taxCode: options.gstRegistered ? line.taxCode : "GST_FREE",
    })),
    { amountsAre: options.amountsAre, rounding: options.rounding },
  )
  return {
    lines: result.lines.map((line) => ({
      netMinor: line.net.amount,
      gstMinor: line.gst.amount,
      grossMinor: line.gross.amount,
    })),
    subtotalMinor: result.totals.net.amount,
    gstMinor: result.totals.gst.amount,
    totalMinor: result.totals.gross.amount,
    roundingAdjustmentMinor: result.roundingAdjustment.amount,
  }
}
